import { useFormik } from 'formik';
import { HttpError } from 'services/api/types';
import { useIntlByKey } from 'services/i18n/hooks/useIntlByKey';
import { logger } from 'services/logs/logger';
import * as yup from 'yup';

import { FRENCH_PHONE_NUMBER_REGEX } from '../validationSchema';

export enum ProfileInformationFormKeys {
  USER_FIRST_NAME = 'firstName',
  USER_LAST_NAME = 'lastName',
  USER_PHONE_NUMBER = 'phoneNumber',
}

export type ProfileInformation = {
  [ProfileInformationFormKeys.USER_FIRST_NAME]: string;
  [ProfileInformationFormKeys.USER_LAST_NAME]: string;
  [ProfileInformationFormKeys.USER_PHONE_NUMBER]: string;
};

export const DEFAULT_VALUES = {
  [ProfileInformationFormKeys.USER_FIRST_NAME]: '',
  [ProfileInformationFormKeys.USER_LAST_NAME]: '',
  [ProfileInformationFormKeys.USER_PHONE_NUMBER]: '',
};

export const useMyProfileFormik = (
  initialFormValues: ProfileInformation,
  onSubmit: (profileInfo: ProfileInformation) => Promise<void>,
  onSubmitError: (error: HttpError) => void,
) => {
  const myProfileFormIntl = useIntlByKey('my-profile-form');

  const requiredFieldMessage = myProfileFormIntl('form.required-field');
  const invalidPhoneNumberMessage = myProfileFormIntl('form.invalid-phone-number');

  const validationSchema = yup.object({
    [ProfileInformationFormKeys.USER_FIRST_NAME]: yup.string().required(requiredFieldMessage),
    [ProfileInformationFormKeys.USER_LAST_NAME]: yup.string().required(requiredFieldMessage),
    [ProfileInformationFormKeys.USER_PHONE_NUMBER]: yup
      .string()
      .matches(FRENCH_PHONE_NUMBER_REGEX, invalidPhoneNumberMessage)
      .required(requiredFieldMessage),
  });

  return useFormik({
    initialValues: initialFormValues,
    validationSchema,
    onSubmit: async (values: ProfileInformation) => {
      try {
        await onSubmit(values);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        logger.logError('Error submitting formik form', error);
        onSubmitError(error.response as HttpError);
      }
    },
  });
};
